import { useState, useContext, useEffect } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';

import { useSessionData } from '@confluence/session-data';
import { RoutesContext, getSingleParam } from '@confluence/route-manager';
import { expVal } from '@confluence/feature-experiments';

import {
	FeedTypeSelectorRefreshQuery,
	FeedTypeSelectorRefreshMutation,
} from './FeedTypeSelectorRefreshQuery.graphql';
import { FeedType } from './feedTypeEnum';

type UseFeedTypeSelector = (defaultPreference?: FeedType) => {
	loading: boolean;
	queryError?: ApolloError;
	mutationError?: ApolloError;
	feedType: FeedType | string;
	updateFeedType: (feedType: FeedType | string) => void;
	hasSelectedFeedTypePreference: boolean;
};

type UseFeedTypeOverride = () => {
	feedTypeOverride?: FeedType;
	clearFeedTypeOverride: () => void;
};

export const useFeedTypeSelector: (defaultPreference?: FeedType) => {
	feedType: FeedType;
	hasSelectedFeedTypePreference: boolean | StringConstructor;
	mutationError: any;
	queryError: any;
	loading: any;
	updateFeedType: (updatedFeedType: FeedType | string) => void;
} = (defaultPreference = FeedType.FOLLOWING) => {
	const { isLoggedIn } = useSessionData();
	const apolloClient = useApolloClient();
	const {
		data,
		loading,
		error: queryError,
	} = useQuery(FeedTypeSelectorRefreshQuery, {
		skip: !isLoggedIn,
	});

	if (expVal('confluence_home_default_to_popular_feed_exp', 'cohort', 'default') !== 'default') {
		defaultPreference = FeedType.POPULAR;
	}

	// @ts-ignore
	const feedType = data?.userPreferences.feedTab || defaultPreference;

	const [feedTypeSelectorMutation, { error: mutationError }] = useMutation(
		FeedTypeSelectorRefreshMutation,
	);

	const updateFeedType = (updatedFeedType: FeedType | string) => {
		if (isLoggedIn) {
			const variables = {
				feedTab: updatedFeedType as String,
			};

			void feedTypeSelectorMutation({
				// @ts-ignore
				variables,
				optimisticResponse: {
					// @ts-ignore __typename does not exist on autogenerated graphql types
					__typename: 'Mutation',
					updateUserPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						feedTab: updatedFeedType as FeedType,
					},
				},
			}).catch(() => {}); // Ignore the error here since it is handled in useMutation

			// Always write to the cache so the user sees the preference even if there is an error
			apolloClient.writeQuery({
				query: FeedTypeSelectorRefreshQuery,
				data: {
					userPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						...variables, //handles either feedType or feedTab depending on FF
					},
				},
			});
		}
	};

	return {
		loading,
		queryError,
		mutationError,
		feedType,
		updateFeedType,
		// @ts-ignore
		hasSelectedFeedTypePreference: Boolean(data?.userPreferences.feedTab),
	};
};

export const useAnonymousFeedTypeSelector: UseFeedTypeSelector = (
	defaultPreference = FeedType.FOLLOWING,
) => {
	const [feedType, setFeedType] = useState<FeedType | string>(defaultPreference);
	const [hasSelectedFeedTypePreference, setHasSelectedFeedTypePreference] =
		useState<boolean>(false);
	return {
		loading: false,
		feedType,
		updateFeedType: (feedType: FeedType | string) => {
			setFeedType(feedType);
			setHasSelectedFeedTypePreference(true);
		},
		hasSelectedFeedTypePreference,
	};
};

export const useFeedTypeOverride: UseFeedTypeOverride = () => {
	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const feedQueryParam = getSingleParam(getQueryParams(), 'feedType') as FeedType | undefined;
	const feed =
		feedQueryParam && Object.values(FeedType).includes(feedQueryParam) ? feedQueryParam : undefined;
	const [feedTypeOverride, setFeedTypeOverride] = useState<FeedType | undefined>(feed);

	useEffect(() => {
		setQueryParams({ feedType: undefined }, true);
	}, [setQueryParams]);

	const clearFeedTypeOverride = () => {
		setFeedTypeOverride(undefined);
	};
	return {
		feedTypeOverride,
		clearFeedTypeOverride,
	};
};

export { FeedType } from './__types__/FeedTypeSelectorMutation';
